import {Injectable, InjectionToken, Injector} from '@angular/core';
import {defer, Observable, Subject, UnaryFunction} from 'rxjs';
import {distinctUntilChanged, filter, share, switchMap, tap} from 'rxjs/operators';
import {bindWith} from '../../common/rxjs/bind-with';
import {squash} from '../../common/rxjs/squash';
import {sortedIndexByComparator} from '../../common/sorted-index-by-comparator';
import {WIDGET_UPDATES_GATING$} from '../../components/layout/widget/widget-updates-gating';
import {Order} from '../../models/order';
import {OrderStreamingService} from '../components/order-streaming';
import {DisplayOrderBase} from './display-order-base';

export interface DisplayOrderRef<T_DISPLAY_ORDER extends DisplayOrderBase> {
    displayOrder: T_DISPLAY_ORDER;
    index: number;
}

export enum OrderListEventType {
    LIST_CLEARED,
    LIST_UPDATED,
    DISPLAY_ORDER_DELETED,
    DISPLAY_ORDER_CREATED_OR_UPDATED,
    DISPLAY_ORDER_CREATED,
    DISPLAY_ORDER_UPDATED
}

export interface OrderListEvent<T_DISPLAY_ORDER extends DisplayOrderBase> {
    type: OrderListEventType;
}

export interface OrderListClearedEvent<T_DISPLAY_ORDER extends DisplayOrderBase>
    extends OrderListEvent<T_DISPLAY_ORDER> {

    type: OrderListEventType.LIST_CLEARED;
}

export interface OrderListUpdatedEvent<T_DISPLAY_ORDER extends DisplayOrderBase>
    extends OrderListEvent<T_DISPLAY_ORDER> {

    type: OrderListEventType.LIST_UPDATED;
}

export interface DisplayOrderEvent<T_DISPLAY_ORDER extends DisplayOrderBase>
    extends OrderListEvent<T_DISPLAY_ORDER> {

    displayOrder: T_DISPLAY_ORDER;
    displayOrderIndex: number;
}

export interface DisplayOrderDeletedEvent<T_DISPLAY_ORDER extends DisplayOrderBase>
    extends OrderListEvent<T_DISPLAY_ORDER>, DisplayOrderEvent<T_DISPLAY_ORDER> {

    type: OrderListEventType.DISPLAY_ORDER_DELETED;
}

export interface DisplayOrderCreatedOrUpdatedEvent<T_DISPLAY_ORDER extends DisplayOrderBase>
    extends OrderListEvent<T_DISPLAY_ORDER>, DisplayOrderEvent<T_DISPLAY_ORDER> {

    type: OrderListEventType.DISPLAY_ORDER_CREATED_OR_UPDATED;
}

export interface DisplayOrderCreatedEvent<T_DISPLAY_ORDER extends DisplayOrderBase>
    extends OrderListEvent<T_DISPLAY_ORDER>, DisplayOrderEvent<T_DISPLAY_ORDER> {

    type: OrderListEventType.DISPLAY_ORDER_CREATED;
}

export interface DisplayOrderUpdatedEvent<T_DISPLAY_ORDER extends DisplayOrderBase>
    extends OrderListEvent<T_DISPLAY_ORDER>, DisplayOrderEvent<T_DISPLAY_ORDER> {

    type: OrderListEventType.DISPLAY_ORDER_UPDATED;
}

export type OrderListEventSubject<T_DISPLAY_ORDER extends DisplayOrderBase> = Subject<OrderListEvent<T_DISPLAY_ORDER>>;

export type CommonOrderListEventHandler<T_DISPLAY_ORDER extends DisplayOrderBase> =
    (event: OrderListEvent<T_DISPLAY_ORDER>) => void;

export interface OrderListEventHandlers<T_DISPLAY_ORDER extends DisplayOrderBase> {
    [OrderListEventType.LIST_CLEARED]: (event: OrderListClearedEvent<T_DISPLAY_ORDER>) => void;
    [OrderListEventType.LIST_UPDATED]: (event: OrderListUpdatedEvent<T_DISPLAY_ORDER>) => void;
    [OrderListEventType.DISPLAY_ORDER_DELETED]: (event: DisplayOrderDeletedEvent<T_DISPLAY_ORDER>) => void;
    [OrderListEventType.DISPLAY_ORDER_CREATED_OR_UPDATED]:
        (event: DisplayOrderCreatedOrUpdatedEvent<T_DISPLAY_ORDER>) => void;
    [OrderListEventType.DISPLAY_ORDER_CREATED]: (event: DisplayOrderCreatedEvent<T_DISPLAY_ORDER>) => void;
    [OrderListEventType.DISPLAY_ORDER_UPDATED]: (event: DisplayOrderUpdatedEvent<T_DISPLAY_ORDER>) => void;
}

export function handleOrderListEvents<T_DISPLAY_ORDER extends DisplayOrderBase>(
    eventHandlers: Partial<OrderListEventHandlers<T_DISPLAY_ORDER>>
): UnaryFunction<Observable<OrderListEvent<T_DISPLAY_ORDER>>, Observable<OrderListEvent<T_DISPLAY_ORDER>>> {
    return tap(event => {
        const eventHandler = eventHandlers[event.type] as CommonOrderListEventHandler<T_DISPLAY_ORDER>;

        if (eventHandler) {
            eventHandler(event);
        }
    });
}

export interface DisplayOrderFactoryBase<T_DISPLAY_ORDER extends DisplayOrderBase> {
    createDisplayOrder(order: Order): T_DISPLAY_ORDER;
}

export const ORDER_LIST_DISPLAY_ORDER_FACTORY = new InjectionToken('ORDER_LIST_DISPLAY_ORDER_FACTORY');

@Injectable()
export class OrderListService<T_DISPLAY_ORDER extends DisplayOrderBase> {

    public displayOrders: T_DISPLAY_ORDER[];

    /**
     * Input parameter (optional).
     * The function which will be used for sorting the order list.
     */
    public compareDisplayOrders: (displayOrder1: T_DISPLAY_ORDER, displayOrder2: T_DISPLAY_ORDER) => number;

    /**
     * Input parameter (required).
     */
    public filterFn$: Observable<(order: Order) => boolean>;

    /**
     * Input parameter (optional).
     * The function which determines if an order should not be appended to the list,
     * or should be removed from the end of the list.
     * Used for limiting list size.
     */
    public shouldDisplayOrderBeTrimmed: (
        displayOrder: T_DISPLAY_ORDER,
        list: T_DISPLAY_ORDER[],
        indexInList: number
    ) => boolean;

    public readonly orderListEvent$ = this.live();

    protected readonly displayOrderRefMapByClOrdId: Map<string, DisplayOrderRef<T_DISPLAY_ORDER>> = new Map();

    private readonly orderStreamingService: OrderStreamingService = this.injector.get(OrderStreamingService);
    private readonly displayOrderFactory: DisplayOrderFactoryBase<T_DISPLAY_ORDER> =
                         this.injector.get<DisplayOrderFactoryBase<T_DISPLAY_ORDER>>(ORDER_LIST_DISPLAY_ORDER_FACTORY);

    /**
     * @param injector {Injector} used in cases when a successor needs the service which couldn't be injected via
     * constructor's arguments due to circular dependencies.
     */
    constructor(
        protected injector: Injector
    ) {}

    public configure(config: Partial<OrderListService<T_DISPLAY_ORDER>>): OrderListService<T_DISPLAY_ORDER> {
        return Object.assign(this, config);
    }

    protected live(): Observable<OrderListEvent<T_DISPLAY_ORDER>> {
        return defer(() => {
            const eventSubject$ = new Subject<OrderListEvent<T_DISPLAY_ORDER>>();

            return this.createStreamForEventSubject(eventSubject$);
        }).pipe(
            share()
        );
    }

    protected createStreamForEventSubject(
        eventSubject$: OrderListEventSubject<T_DISPLAY_ORDER>
    ): Observable<OrderListEvent<T_DISPLAY_ORDER>> {
        return eventSubject$.pipe(
            bindWith(
                this.listenToOrderStream(eventSubject$),
                this.listenToDeprecateOrderStream(eventSubject$)
            )
        );
    }

    protected createDisplayOrder(
        order: Order,
        eventSubject$: OrderListEventSubject<T_DISPLAY_ORDER>
    ): T_DISPLAY_ORDER | undefined {
        const displayOrder: T_DISPLAY_ORDER = this.displayOrderFactory.createDisplayOrder(order);
        const displayOrderIndex = this.sortedOrderInsert(displayOrder, eventSubject$);
        const isInserted = displayOrderIndex >= 0;

        if (isInserted) {
            this.emitEvent<DisplayOrderCreatedEvent<T_DISPLAY_ORDER>>(eventSubject$, {
                type: OrderListEventType.DISPLAY_ORDER_CREATED,
                displayOrder,
                displayOrderIndex
            })

            this.emitEvent<DisplayOrderCreatedOrUpdatedEvent<T_DISPLAY_ORDER>>(eventSubject$, {
                type: OrderListEventType.DISPLAY_ORDER_CREATED_OR_UPDATED,
                displayOrder,
                displayOrderIndex
            });
        }

        return isInserted ? displayOrder : undefined;
    }

    /**
     * Helper to emit events with event typings, without needing to create event variable.
     */
    protected emitEvent<E extends OrderListEvent<T_DISPLAY_ORDER>>(
        eventSubject$: OrderListEventSubject<T_DISPLAY_ORDER>,
        event: E
    ): void {
        eventSubject$.next(event);
    }

    private listenToOrderStream(eventSubject$: OrderListEventSubject<T_DISPLAY_ORDER>): Observable<Order> {
        const rawStream$: Observable<Order> = this.orderStreamingService.order$.pipe(
            squash({
                closingNotifier: WIDGET_UPDATES_GATING$,
                groupBy: (order: Order) => order.clOrdId,
                spread: true
            }) as (source: Observable<Order>) => Observable<Order>
        );

        return this.filterFn$.pipe(
            distinctUntilChanged(),
            tap(() => {
                this.displayOrders.length = 0;
                this.displayOrderRefMapByClOrdId.clear();

                this.emitEvent<OrderListClearedEvent<T_DISPLAY_ORDER>>(eventSubject$, {
                    type: OrderListEventType.LIST_CLEARED
                });
                this.emitEvent<OrderListUpdatedEvent<T_DISPLAY_ORDER>>(eventSubject$, {
                    type: OrderListEventType.LIST_UPDATED
                });
            }),
            switchMap(filterFn => filterFn
                ? rawStream$.pipe(
                    filter(order => {
                        const isAcceptable: boolean = filterFn(order);

                        if (!isAcceptable) {
                            this.deleteDisplayOrder(order, eventSubject$);
                        }

                        return isAcceptable;
                    })
                )
                : rawStream$
            ),
            tap((order: Order) => this.createOrUpdateDisplayOrder(order, eventSubject$))
        );
    }

    private listenToDeprecateOrderStream(eventSubject$: OrderListEventSubject<T_DISPLAY_ORDER>): Observable<Order> {
        return this.orderStreamingService.deprecateOrder$.pipe(
            squash({
                closingNotifier: WIDGET_UPDATES_GATING$,
                groupBy: (order: Order) => order.clOrdId,
                spread: true
            }),
            tap((order: Order) => this.deleteDisplayOrder(order, eventSubject$))
        );
    }

    private deleteDisplayOrder(
        order: Order,
        eventSubject$: OrderListEventSubject<T_DISPLAY_ORDER>,
        notifyListUpdated: boolean = true
    ): void {
        const ref: DisplayOrderRef<T_DISPLAY_ORDER> = this.displayOrderRefMapByClOrdId.get(order.clOrdId);

        if (ref) {
            this.displayOrderRefMapByClOrdId.delete(order.clOrdId);
            this.displayOrders.splice(ref.index, 1);

            this.updateDisplayOrderIndexesFrom(ref.index, -1);

            this.emitEvent<DisplayOrderDeletedEvent<T_DISPLAY_ORDER>>(eventSubject$, {
                type: OrderListEventType.DISPLAY_ORDER_DELETED,
                displayOrder: ref.displayOrder,
                displayOrderIndex: ref.index
            });

            if (notifyListUpdated) {
                this.emitEvent<OrderListUpdatedEvent<T_DISPLAY_ORDER>>(eventSubject$, {
                    type: OrderListEventType.LIST_UPDATED
                });
            }
        }
    }

    private createOrUpdateDisplayOrder(order: Order, eventSubject$: OrderListEventSubject<T_DISPLAY_ORDER>): void {
        const knownRef: DisplayOrderRef<T_DISPLAY_ORDER> = this.displayOrderRefMapByClOrdId.get(order.clOrdId);
        let isListUpdated: boolean;

        if (knownRef) {
            this.updateDisplayOrder(knownRef, order, eventSubject$);
            isListUpdated = true;
        } else {
            isListUpdated = !!this.createDisplayOrder(order, eventSubject$);
        }

        if (isListUpdated) {
            this.emitEvent<OrderListUpdatedEvent<T_DISPLAY_ORDER>>(eventSubject$, {
                type: OrderListEventType.LIST_UPDATED
            });
        }
    }

    private updateDisplayOrder(
        knownRef: DisplayOrderRef<T_DISPLAY_ORDER>,
        order: Order,
        eventSubject$: OrderListEventSubject<T_DISPLAY_ORDER>
    ): T_DISPLAY_ORDER {
        const displayOrder: T_DISPLAY_ORDER = knownRef.displayOrder;
        const displayOrderUpdate = this.displayOrderFactory.createDisplayOrder(order);
        const sortingRank = this.invokeCompareDisplayOrders(displayOrderUpdate, displayOrder);
        let isOrderUpdated: boolean = true;

        Object.assign(
            displayOrder,
            displayOrderUpdate
        );

        let displayOrderIndex = knownRef.index;

        if (sortingRank !== 0) {
            const newDisplayOrderIndex = this.updateOrderPositionInList(knownRef, displayOrder, eventSubject$);

            if (newDisplayOrderIndex < 0) {
                isOrderUpdated = false;

                this.emitEvent<DisplayOrderDeletedEvent<T_DISPLAY_ORDER>>(eventSubject$, {
                    type: OrderListEventType.DISPLAY_ORDER_DELETED,
                    displayOrder,
                    displayOrderIndex
                });
            }

            displayOrderIndex = newDisplayOrderIndex;
        }

        if (isOrderUpdated) {
            this.emitEvent<DisplayOrderUpdatedEvent<T_DISPLAY_ORDER>>(eventSubject$, {
                type: OrderListEventType.DISPLAY_ORDER_UPDATED,
                displayOrder,
                displayOrderIndex
            });

            this.emitEvent<DisplayOrderCreatedOrUpdatedEvent<T_DISPLAY_ORDER>>(eventSubject$,{
                type: OrderListEventType.DISPLAY_ORDER_CREATED_OR_UPDATED,
                displayOrder,
                displayOrderIndex
            });
        }

        return displayOrder;
    }

    private updateDisplayOrderIndexesFrom(indexUpdateFrom: number, increment: number): void {
        for (; indexUpdateFrom < this.displayOrders.length; indexUpdateFrom++) {
            const ref = this.displayOrderRefMapByClOrdId.get(this.displayOrders[indexUpdateFrom].order.clOrdId);

            ref.index += increment;
        }
    }

    private getOrderIndexForInsertion(displayOrder: T_DISPLAY_ORDER): number {
        return this.compareDisplayOrders
            ? sortedIndexByComparator(this.displayOrders, displayOrder, this.compareDisplayOrders.bind(this))
            : this.displayOrders.length;
    }

    private invokeCompareDisplayOrders(displayOrder1: T_DISPLAY_ORDER, displayOrder2: T_DISPLAY_ORDER): number {
        return this.compareDisplayOrders
            ? this.compareDisplayOrders(displayOrder1, displayOrder2)
            : 0;
    }

    private invokeShouldDisplayOrderBeTrimmed(displayOrder: T_DISPLAY_ORDER, indexInList: number): boolean {
        return !!this.shouldDisplayOrderBeTrimmed
            && this.shouldDisplayOrderBeTrimmed(displayOrder, this.displayOrders, indexInList);
    }

    private sortedOrderInsert(
        displayOrder: T_DISPLAY_ORDER,
        eventSubject$: OrderListEventSubject<T_DISPLAY_ORDER>
    ): number {
        const insertionIndex = this.getOrderIndexForInsertion(displayOrder);
        const isAppending = insertionIndex >= this.displayOrders.length;
        let displayOrderIndex = insertionIndex;

        if (isAppending) {
            if (this.invokeShouldDisplayOrderBeTrimmed(displayOrder, -1)) {
                displayOrderIndex = -1;
            } else {
                this.displayOrders.push(displayOrder);
            }
        } else {
            this.updateDisplayOrderIndexesFrom(displayOrderIndex, 1);
            this.displayOrders.splice(displayOrderIndex, 0, displayOrder);
        }

        if (displayOrderIndex >= 0) {
            this.displayOrderRefMapByClOrdId.set(displayOrder.order.clOrdId, {
                displayOrder,
                index: displayOrderIndex
            });

            // Trim last order if needed.
            if (!isAppending) {
                this.trimLastDisplayOrderIfNeeded(eventSubject$);
            }
        }

        return displayOrderIndex;
    }

    private updateOrderPositionInList(
        knownRef: DisplayOrderRef<T_DISPLAY_ORDER>,
        displayOrder: T_DISPLAY_ORDER,
        eventSubject$: OrderListEventSubject<T_DISPLAY_ORDER>
    ): number {
        const oldIndex = knownRef.index;

        this.displayOrders.splice(oldIndex, 1);
        this.updateDisplayOrderIndexesFrom(oldIndex, -1);

        const newDisplayOrderIndex = this.sortedOrderInsert(displayOrder, eventSubject$);

        if (newDisplayOrderIndex < 0) {
            this.displayOrderRefMapByClOrdId.delete(displayOrder.order.clOrdId);
        }

        return newDisplayOrderIndex;
    }

    private trimLastDisplayOrderIfNeeded(eventSubject$: OrderListEventSubject<T_DISPLAY_ORDER>): void {
        const lastIndex = this.displayOrders.length - 1;
        const lastDisplayOrder = this.displayOrders[lastIndex];

        if (lastDisplayOrder && this.invokeShouldDisplayOrderBeTrimmed(lastDisplayOrder, lastIndex)) {
            this.deleteDisplayOrder(lastDisplayOrder.order, eventSubject$, false);
        }
    }

}
